import React, { useEffect, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import FalconCardHeader from 'components/common/FalconCardHeader';
import axios from 'helpers/api';
import Flex from 'components/common/Flex';
import moment from 'moment';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import ShimmerUI from 'components/ShimmerUI/PatientShimmer';
import isHiddenFacility from 'helpers/isHiddenFacility';
import CycleAttestation from 'components/delivery/cycle/CycleAttestation';

const CycleAttestationReport = () => {
    const [options, setOptions] = useState([{ key: 'Select Facility', value: '' }]);
    const [cycledateoptions, setCycleDateOptions] = useState([{ key: 'Select CycleDate', value: '' }]);
    const [selectedFacility, setSelectedFacility] = useState(null);
    const [facilitydata, setFacilityData] = useState(null);
    const [initialCycleDate, setInitialCycleDate] = useState('');
    const [selectedCycleData, setSelectedCycleData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        populateData();
    }, []);

    async function populateData() {
        let results = (JSON.parse(localStorage.getItem('facilitiesData')) || []).filter(f => !isHiddenFacility(f));
        setOptions([
            { key: 'Select Facility', value: '' },
            ...results
        ]);

        let defaultFacility = results[0];
        if (defaultFacility && defaultFacility.value) {
            setSelectedFacility(defaultFacility);
            setInitialCycleStartDate(defaultFacility.value);
        }
    }

    const setInitialCycleStartDate = async (facilityId) => {
        if (!facilityId) {
            setFacilityData(null);
            setCycleDateOptions([{ key: 'Select Cycledate', value: '' }]);
            setInitialCycleDate('');
            setSelectedCycleData(null);
            return;
        }

        try {
            setLoading(true);
            let tokenStr = localStorage.getItem('accessToken');

            const { data } = await axios.get(
                `${process.env.REACT_APP_SERVER_URL}/cycles/attestation-checklist/` + facilityId,
                { headers: { Authorization: `Bearer ${tokenStr}` } }
            );

            let cycleData = data?.cycleData;
            let cycleHeadersIds = data?.cycleHeadersIds || (cycleData ? Object.keys(cycleData) : []);

            if (!cycleData || Object.keys(cycleData).length === 0) {
                setFacilityData(null);
                setCycleDateOptions([{ key: 'Select Cycledate', value: '' }]);
                setInitialCycleDate('');
                setSelectedCycleData(null);
                return;
            }

            setFacilityData(cycleData);

            let cycleResults = [];
            cycleHeadersIds.forEach((cyclekey) => {
                if (cycleData[cyclekey]) {
                    cycleResults.push({
                        key: moment(cyclekey, 'YYYY-MM-DD').format('MM/DD/YY'),
                        value: cyclekey
                    });
                }
            });

            for (let cyclekey in cycleData) {
                if (!cycleResults.some(r => r.value === cyclekey)) {
                    cycleResults.push({
                        key: moment(cyclekey, 'YYYY-MM-DD').format('MM/DD/YY'),
                        value: cyclekey
                    });
                }
            }

            setCycleDateOptions([
                { key: 'Select Cycledate', value: '' },
                ...cycleResults
            ]);

            if (cycleResults.length > 0) {
                const firstDate = cycleResults[0].value;
                setInitialCycleDate(firstDate);
                setCycleDate(firstDate, cycleData);
            } else {
                setInitialCycleDate('');
                setSelectedCycleData(null);
            }
        } catch (error) {
            console.error('Error loading cycle attestation checklist:', error);
            setFacilityData(null);
            setCycleDateOptions([{ key: 'Select Cycledate', value: '' }]);
            setInitialCycleDate('');
            setSelectedCycleData(null);
        } finally {
            setLoading(false);
        }
    };

    const setCycleDate = (cycleDate, currentFacilityData) => {
        const dataToUse = currentFacilityData || facilitydata;
        if (!dataToUse || !dataToUse[cycleDate]) {
            setSelectedCycleData(null);
            return;
        }

        let singleCycleList = dataToUse[cycleDate];
        let cycleObj = Array.isArray(singleCycleList) ? singleCycleList[0] : singleCycleList;
        setSelectedCycleData(cycleObj);
    };

    const handleFacilityFieldChange = (selectedOption) => {
        if (selectedOption && selectedOption.value) {
            setSelectedFacility(selectedOption);
            setInitialCycleStartDate(selectedOption.value);
        } else {
            setSelectedFacility(null);
            setFacilityData(null);
            setCycleDateOptions([{ key: 'Select Cycledate', value: '' }]);
            setInitialCycleDate('');
            setSelectedCycleData(null);
        }
    };

    const handleCycleDate = (e) => {
        const selectedDate = e.target.value;
        setInitialCycleDate(selectedDate);
        setCycleDate(selectedDate, facilitydata);
    };

    const breadcrumbItems = [
        { name: 'Dashboard', href: '/main', isActive: false },
        { name: 'Cycle Attestation', href: '', isActive: true }
    ];

    return (
        <>
            <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
            <Row className="g-3">
                <Col xs="12">
                    <Card className="shadow-none">
                        <FalconCardHeader
                            title="Cycle Attestation"
                            titleTag="h6"
                            className="py-2"
                            light
                        />
                        <Card.Body
                            className="h-100"
                            as={Flex}
                            direction="column"
                            alignItems="between"
                        >
                            <Row className="align-items-end">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="required">Facility</Form.Label>
                                        <ThemedSelect
                                            name="facility"
                                            closeMenuOnSelect={true}
                                            placeholder="Select Facility"
                                            className="form-control-sm"
                                            classNamePrefix="react-select"
                                            options={options}
                                            value={selectedFacility}
                                            getOptionLabel={option => option.key}
                                            getOptionValue={option => option.value}
                                            onChange={handleFacilityFieldChange}
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    borderColor: '#ced4da',
                                                    backgroundColor: 'white',
                                                    height: 'calc(1.5em + 0.75rem + 2px)',
                                                    minHeight: '38px',
                                                    boxShadow: 'none',
                                                    '&:hover': { borderColor: '#80bdff' }
                                                }),
                                                option: (provided, { isFocused, isSelected }) => ({
                                                    ...provided,
                                                    backgroundColor: isSelected ? '#e9ecef !important' : isFocused ? '#f8f9fa !important' : 'white !important',
                                                    color: isSelected ? '#212529 !important' : '#212529 !important',
                                                    cursor: 'pointer'
                                                }),
                                                singleValue: (provided) => ({
                                                    ...provided,
                                                    color: '#212529'
                                                })
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="required mb-0">Cycle Date</Form.Label>
                                        <Form.Select
                                            size="sm"
                                            name="cycle"
                                            value={initialCycleDate || ''}
                                            onChange={handleCycleDate}
                                            disabled={loading || cycledateoptions.length <= 1}
                                        >
                                            {cycledateoptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.key}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <br />
            {loading ? (
                <ShimmerUI />
            ) : selectedCycleData ? (
                <Card>
                    <Card.Body className="p-3">
                        <CycleAttestation
                            attestations={selectedCycleData?.attestations}
                            loading={loading}
                        />
                    </Card.Body>
                </Card>
            ) : (
                <Card>
                    <p className="text-danger w-100 text-center p-4 mb-0 fw-bold">
                        There is no data available for this facility, Please select another one
                    </p>
                </Card>
            )}
        </>
    );
};

export default CycleAttestationReport;
